// =============================================================================
// ZONE EDITOR 3D - DISPLAY CASE (ESPOSITORE)
// Tavolo con piano inclinato fisso a 30°
// =============================================================================

import { useMemo } from 'react'
import { Html } from '@react-three/drei'
import { mmToSceneScale, degreesToRadians, FIXED_SURFACE_TILT_DEG } from './utils'
import type { WorkspaceConfig } from '../../types'

interface DisplayCaseProps {
  workspace: WorkspaceConfig
  tableHeight: number
  surfaceTiltDeg?: number
}

export function DisplayCase({ workspace, tableHeight, surfaceTiltDeg = FIXED_SURFACE_TILT_DEG }: DisplayCaseProps) {
  const w = mmToSceneScale(workspace.width)
  const h = mmToSceneScale(workspace.height)

  const legRadius = 0.008
  const legInset = 0.02
  const topThickness = 0.012
  const surfaceTiltRad = degreesToRadians(surfaceTiltDeg)

  // Back edge rises by h * sin(tilt)
  const backRise = h * Math.sin(surfaceTiltRad)
  const hProjected = h * Math.cos(surfaceTiltRad) // depth on the ground plane

  // Grid on tilted surface (local coords: surface is flat, tilt handled by parent group)
  const gridLines = useMemo(() => {
    const points: number[] = []
    let spacing = 0.1
    const maxDim = Math.max(w, h)
    if (maxDim < 0.5) spacing = 0.05
    else if (maxDim > 2) spacing = 0.25

    for (let x = 0; x <= w + 0.001; x += spacing) {
      points.push(x, 0.001, 0, x, 0.001, -h)
    }
    for (let y = 0; y <= h + 0.001; y += spacing) {
      points.push(0, 0.001, -y, w, 0.001, -y)
    }
    return new Float32Array(points)
  }, [w, h])

  const borderPoints = useMemo(() => new Float32Array([
    0, 0.002, 0,
    w, 0.002, 0,
    w, 0.002, -h,
    0, 0.002, -h,
    0, 0.002, 0,
  ]), [w, h])

  // Front legs: tableHeight, Back legs: tableHeight + backRise
  const frontLegH = tableHeight - topThickness
  const backLegH = tableHeight + backRise - topThickness

  return (
    <group>
      {/* ============= FRONT LEGS ============= */}
      {[[legInset, -legInset], [w - legInset, -legInset]].map(([lx, lz], i) => (
        <mesh key={`fl-${i}`} position={[lx, frontLegH / 2, lz]}>
          <cylinderGeometry args={[legRadius, legRadius, frontLegH, 8]} />
          <meshStandardMaterial color="#4a3a2a" roughness={0.8} />
        </mesh>
      ))}

      {/* ============= BACK LEGS ============= */}
      {[[legInset, -hProjected + legInset], [w - legInset, -hProjected + legInset]].map(([lx, lz], i) => (
        <mesh key={`bl-${i}`} position={[lx, backLegH / 2, lz]}>
          <cylinderGeometry args={[legRadius, legRadius, backLegH, 8]} />
          <meshStandardMaterial color="#4a3a2a" roughness={0.8} />
        </mesh>
      ))}

      {/* ============= TILTED SURFACE GROUP ============= */}
      {/* Pivot at front edge, tilts backward (back goes up) */}
      <group position={[0, tableHeight, 0]} rotation={[surfaceTiltRad, 0, 0]}>
        {/* Table top (wooden panel) */}
        <mesh position={[w / 2, -topThickness / 2, -h / 2]}>
          <boxGeometry args={[w + 0.01, topThickness, h + 0.01]} />
          <meshStandardMaterial color="#5a4a3a" roughness={0.7} />
        </mesh>

        {/* Surface (green felt) */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[w / 2, 0.0005, -h / 2]}>
          <planeGeometry args={[w - 0.004, h - 0.004]} />
          <meshStandardMaterial color="#2a6a45" roughness={0.5} />
        </mesh>

        {/* Grid */}
        <lineSegments>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              array={gridLines}
              count={gridLines.length / 3}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#1e5a3a" transparent opacity={0.25} />
        </lineSegments>

        {/* Border */}
        <line>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              array={borderPoints}
              count={borderPoints.length / 3}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#4aaa8a" transparent opacity={0.6} />
        </line>

        {/* Dimension labels */}
        <Html
          position={[w / 2, 0.01, 0.02]}
          center
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          <div style={{
            color: '#8aa',
            fontSize: 9,
            fontFamily: 'monospace',
            whiteSpace: 'nowrap'
          }}>
            {workspace.width}mm
          </div>
        </Html>
        <Html
          position={[-0.02, 0.01, -h / 2]}
          center
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          <div style={{
            color: '#8aa',
            fontSize: 9,
            fontFamily: 'monospace',
            whiteSpace: 'nowrap'
          }}>
            {workspace.height}mm
          </div>
        </Html>
      </group>

      {/* ============= FRONT CROSSBAR ============= */}
      <mesh position={[w / 2, tableHeight - topThickness, 0]}>
        <boxGeometry args={[w, topThickness * 0.5, topThickness * 0.5]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.8} />
      </mesh>

      {/* ============= BACK CROSSBAR ============= */}
      <mesh position={[w / 2, tableHeight + backRise - topThickness, -hProjected]}>
        <boxGeometry args={[w, topThickness * 0.5, topThickness * 0.5]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.8} />
      </mesh>
    </group>
  )
}
